import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './index.css';
import { initializeFirebase } from './config/firebase';
import ErrorBoundary from './components/ErrorBoundary';
import { ThemeProvider } from './contexts/ThemeContext';
import { logger } from '@/utils/logger';
import {
  createStaleChunkRecovery,
  isStaleChunkError,
  showStaleBundleBanner,
} from '@/utils/staleChunkRecovery';

// Global error handler to suppress non-critical Stripe analytics errors
// These errors occur when browser extensions block Stripe's analytics endpoint
// They don't affect payment processing functionality
if (typeof window !== 'undefined') {
  // After a deploy, a still-open tab can request an old lazy-route chunk that no
  // longer exists, leaving the user on a blank screen. Reload once (guarded against
  // a loop) so they silently land on the fresh bundle (S8-36). If that reload did
  // not help, show a visible Reload banner instead of a silent no-op (#1304).
  const recoverFromStaleChunk = createStaleChunkRecovery({
    storage: (() => { try { return window.sessionStorage; } catch { return null; } })(),
    reload: () => {
      logger.warn('A stale app chunk failed to load; reloading to fetch the latest version.');
      window.location.reload();
    },
    onRepeatFailure: () => {
      logger.warn('Stale app chunk persisted after reload; prompting the user to reload manually.');
      showStaleBundleBanner();
    },
  });

  // Vite emits this when a dynamically-imported module fails to preload.
  window.addEventListener('vite:preloadError', (event: Event) => {
    event.preventDefault();
    recoverFromStaleChunk();
  });

  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason;
    const errorMessage = error?.message || String(error) || '';
    const errorStack = error?.stack || '';

    // Stale-chunk dynamic-import failure after a deploy — reload to recover (S8-36).
    if (isStaleChunkError(errorMessage)) {
      event.preventDefault();
      recoverFromStaleChunk();
      return;
    }

    // Suppress non-critical Stripe analytics errors (r.stripe.com/b)
    // These are caused by browser extensions blocking analytics and don't affect payments
    if (
      errorMessage.includes('r.stripe.com') ||
      errorMessage.includes('Failed to fetch') && errorStack.includes('stripe')
    ) {
      // Check if it's specifically the analytics endpoint
      if (errorMessage.includes('r.stripe.com/b') || errorStack.includes('r.stripe.com/b')) {
        // Suppress this error - it's just analytics being blocked
        event.preventDefault();
        logger.debug('Stripe analytics endpoint blocked by browser extension (non-critical)');
        return;
      }
    }

    // For other Stripe-related errors, log them but don't suppress
    // They might be actual payment processing errors
  });
}

// Initialize Firebase
initializeFirebase();

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <ThemeProvider>
        <App />
      </ThemeProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
